import { combineReducers } from 'redux';
import { call, put, takeLatest } from 'redux-saga/effects';
import settingApi from '../api';
import {
  createApiActionCreators,
  createActionCreator,
  createReducer,
  createActionType,
  REQUEST,
  SUCCESS,
  FAILURE,
} from '../../../common/utils/reduxHelpers';
import { message } from 'antd';

const HIDDEN_SETTINGS = ['PAYMENT_IBAN', 'FIO_ID_LAST', 'INTEREST_RANGE'];

const SETTING_CATEGORIES = {
  debtor: [
    'DEBTOR_ONBOARDING_CALCULATOR_MIN',
    'DEBTOR_ONBOARDING_CALCULATOR_MAX',
    'DEBTOR_ONBOARDING_CALCULATOR_MAX_EXISTING',
    'DEBTOR_HIGHER_MAX_LOAN_AMOUNT_MAX_RATING',
    'DEBTOR_HIGHER_MAX_LOAN_AMOUNT_MIN_PAID_LOANS',
    'DEBTOR_ONBOARDING_CALCULATOR_STEP',
    'DEBTOR_CALCULATOR_DEFAULT_AMOUNT',
    'DEBTOR_CALCULATOR_DEFAULT_MONTHS',
    'LOAN_EXPIRATION_DAYS',
    'UNACCEPTED_LOAN_EXPIRATION_DAYS',
    'DUE_NOTICE_1_DAYS',
    'DUE_NOTICE_2_DAYS',
    'REDEMPTION_DAYS',
    'MAX_NUMBER_OF_OPEN_LOANS',
    'SHIFT_PAYMENT_NUMBER',
    'SHIFT_PAYMENT_MONTHS',
    'FIRST_LOAN_PROVISION_FEE',
    'SECOND_LOAN_PROVISION_FEE',
    'OTHER_LOAN_PROVISION_FEE',
    'MINIMAL_LOAN_PROVISION_FEE',
    'LOAN_MAX_MONTHS',
    'LOAN_MIN_MONTHS'
  ],
  creditor: [
    'CREDITOR_ONBOARDING_CALCULATOR_MIN',
    'CREDITOR_ONBOARDING_CALCULATOR_MAX',
    'CREDITOR_ONBOARDING_CALCULATOR_STEP',
    'CREDITOR_CALCULATOR_DEFAULT_AMOUNT',
    'CREDITOR_CALCULATOR_DEFAULT_MONTHS',
    'CREDITOR_ALLOCATION_TRANSFER_FEE',
  ],
  p2p: [],
  loanInsurance: [
    'INSURANCE_A_PRICE_VALUE',
    'INSURANCE_B_PRICE_VALUE',
    'INSURANCE_C_PRICE_VALUE',
    'INSURANCE_A_PRICE_UNIT',
    'INSURANCE_B_PRICE_UNIT',
    'INSURANCE_C_PRICE_UNIT',
    'INSURANCE_A_INTEREST_DISCOUNT',
    'INSURANCE_B_INTEREST_DISCOUNT',
    'INSURANCE_C_INTEREST_DISCOUNT'
  ],
  prescoring: [
    'PRESCORING_ON'
  ],
  biometrics: [
    'BIOMETRICS_ON',
    'BIOMETRIC_THRESHOLD'
  ],
  psd2: [
    'PSD2_INTEGRATION_ON',
    'PSD2_INTEREST_DISCOUNT'
  ],
};

const SETTING_TYPES = {
  'DEBTOR_ONBOARDING_CALCULATOR_MIN': 'number',
  'DEBTOR_ONBOARDING_CALCULATOR_MAX': 'number',
  'DEBTOR_ONBOARDING_CALCULATOR_MAX_EXISTING': 'number',
  'DEBTOR_HIGHER_MAX_LOAN_AMOUNT_MAX_RATING': 'number',
  'DEBTOR_HIGHER_MAX_LOAN_AMOUNT_MIN_PAID_LOANS': 'number',
  'DEBTOR_ONBOARDING_CALCULATOR_STEP': 'number',
  'DEBTOR_CALCULATOR_DEFAULT_AMOUNT': 'number',
  'DEBTOR_CALCULATOR_DEFAULT_MONTHS': 'number',
  'LOAN_EXPIRATION_DAYS': 'number',
  'UNACCEPTED_LOAN_EXPIRATION_DAYS': 'number',
  'DUE_NOTICE_1_DAYS': 'number',
  'DUE_NOTICE_2_DAYS': 'number',
  'REDEMPTION_DAYS': 'number',
  'MAX_NUMBER_OF_OPEN_LOANS': 'number',
  'SHIFT_PAYMENT_NUMBER': 'number',
  'SHIFT_PAYMENT_MONTHS': 'number',
  'FIRST_LOAN_PROVISION_FEE': 'number',
  'SECOND_LOAN_PROVISION_FEE': 'number',
  'OTHER_LOAN_PROVISION_FEE': 'number',
  'MINIMAL_LOAN_PROVISION_FEE': 'number',
  'LOAN_MAX_MONTHS': 'number',
  'LOAN_MIN_MONTHS': 'number',
  'CREDITOR_ONBOARDING_CALCULATOR_MIN': 'number',
  'CREDITOR_ONBOARDING_CALCULATOR_MAX': 'number',
  'CREDITOR_ONBOARDING_CALCULATOR_STEP': 'number',
  'CREDITOR_CALCULATOR_DEFAULT_AMOUNT': 'number',
  'CREDITOR_CALCULATOR_DEFAULT_MONTHS': 'number',
  'CREDITOR_ALLOCATION_TRANSFER_FEE': 'number',
  'INSURANCE_A_PRICE_VALUE': 'number',
  'INSURANCE_B_PRICE_VALUE': 'number',
  'INSURANCE_C_PRICE_VALUE': 'number',
  'INSURANCE_A_PRICE_UNIT': 'string',
  'INSURANCE_B_PRICE_UNIT': 'string',
  'INSURANCE_C_PRICE_UNIT': 'string',
  'INSURANCE_A_INTEREST_DISCOUNT': 'number',
  'INSURANCE_B_INTEREST_DISCOUNT': 'number',
  'INSURANCE_C_INTEREST_DISCOUNT': 'number',
  'PRESCORING_ON': 'boolean',
  'BIOMETRICS_ON': 'boolean',
  'BIOMETRIC_THRESHOLD': 'number',
  'ACCOUNT_BALANCE_REPORT_EMAIL_LIST': 'string',
  'DAILY_REPORT_EMAIL_LIST': 'string',
  'NEW_ID_CARD_NUMBER_EMAIL_LIST': 'string',
  'PENALTY_FEE_REPORT_LIST': 'string',
  'UNPAID_LOANS_REPORT_EMAIL_LIST': 'string',
  'PSD2_INTEGRATION_ON': 'boolean',
  'PSD2_INTEREST_DISCOUNT': 'number',
}

const getSettingCategory = setting =>
  Object.entries(SETTING_CATEGORIES).reduce(
    (acc, [category, keys]) => (keys.includes(setting.key) ? category : acc),
    'other'
  );

const getSettingType = key => {
  if (Object.keys(SETTING_TYPES).includes(key)) {
    return SETTING_TYPES[key];
  }

  return 'string'
}

/**
 * ACTION TYPES
 */
export const FETCH_SETTINGS = 'settingManagement/FETCH_SETTINGS';
export const FETCH_SETTING = 'settingManagement/FETCH_SETTING';
export const UPDATE_SETTING = 'settingManagement/UPDATE_SETTING';
export const SET_SETTING_EDITABLE = 'settingManagement/SET_SETTING_EDITABLE';
export const FETCH_INTEREST_SETTINGS = 'settingManagement/FETCH_INTEREST_SETTINGS';
export const UPDATE_INTEREST_SETTINGS = 'settingManagement/UPDATE_INTEREST_SETTINGS';
export const FETCH_INTEREST_SETTINGS_HISTORY = 'settingManagement/FETCH_INTEREST_SETTINGS_HISTORY';
export const FETCH_GENERAL_CONTRACT_HISTORY = 'settingManagement/FETCH_GENERAL_CONTRACT_HISTORY';
export const NEW_GENERAL_CONTRACT_ENTRY = 'settingManagement/NEW_GENERAL_CONTRACT_ENTRY';

/**
 * ACTIONS
 */
export const fetchSettingsActions = createApiActionCreators(FETCH_SETTINGS);
export const fetchSettingActions = createApiActionCreators(FETCH_SETTING);
export const updateSettingActions = createApiActionCreators(UPDATE_SETTING);
export const setSettingEditableAction = createActionCreator(SET_SETTING_EDITABLE);
export const fetchInterestSettingsActions = createApiActionCreators(FETCH_INTEREST_SETTINGS);
export const updateInterestSettingsActions = createApiActionCreators(UPDATE_INTEREST_SETTINGS);
export const fetchInterestSettingsHistoryActions = createApiActionCreators(FETCH_INTEREST_SETTINGS_HISTORY);
export const fetchGeneralContractHistoryActions = createApiActionCreators(FETCH_GENERAL_CONTRACT_HISTORY);
export const newGeneralContractEntryActions = createApiActionCreators(NEW_GENERAL_CONTRACT_ENTRY);

/**
 * REDUCERS
 */
const initialState = {
  setting: null,
  settings: {
    items: [],
  },
  generalContractHistory: {
    items: [],
  },
  generalContractHistoryEntry: null,
  interestSettings: [],
  interestSettingsHistory: [],
  settingEditable: '',
};

const setting = createReducer(initialState.setting, {
  [FETCH_SETTING]: {
    [SUCCESS]: (state, payload) => {
      payload.type = getSettingType(payload.key)
    
      return payload
    },
    [FAILURE]: () => null,
  },
  [UPDATE_SETTING]: {
    [SUCCESS]: (state, payload) => {
      payload.type = getSettingType(payload.key)
    
      return payload
    },
  },
});

const settings = createReducer(initialState.settings, {
  [FETCH_SETTINGS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload.data
        .filter(setting => !HIDDEN_SETTINGS.includes(setting.key))
        .map(setting => ({ ...setting, category: getSettingCategory(setting) })),
    }),
    [FAILURE]: state => ({
      ...state,
      items: [],
    }),
  },
});

const settingEditable = createReducer(initialState.settingEditable, {
  [SET_SETTING_EDITABLE]: (state, payload) => payload,
});

const interestSettings = createReducer(initialState.interestSettings, {
  [FETCH_INTEREST_SETTINGS]: {
    [SUCCESS]: (state, payload) => payload,
  },
  [UPDATE_INTEREST_SETTINGS]: {
    [SUCCESS]: (state, payload) => payload,
  },
});


const interestSettingsHistory = createReducer(initialState.interestSettingsHistory, {
  [FETCH_INTEREST_SETTINGS_HISTORY]: {
    [SUCCESS]: (state, payload) => payload,
  },
});

const generalContractHistory = createReducer(initialState.generalContractHistory, {
  [FETCH_GENERAL_CONTRACT_HISTORY]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload.data
    })
  }
})

const newGeneralContractEntry = createReducer(initialState.generalContractHistoryEntry, {
  [NEW_GENERAL_CONTRACT_ENTRY]: {
    [SUCCESS]: (state, payload) => payload
  }
})

export default combineReducers({
  setting,
  settings,
  settingEditable,
  interestSettings,
  interestSettingsHistory,
  generalContractHistory,
  newGeneralContractEntry
});

/**
 * SELECTORS
 */
export const selectSettingState = state => state.settingManagement;

export const selectSetting = state => selectSettingState(state).setting;
export const selectSettingEditable = state => selectSettingState(state).settingEditable;
export const selectInterestSettings = state => selectSettingState(state).interestSettings;
export const selectInterestSettingsHistory = state => selectSettingState(state).interestSettingsHistory;

export const selectGeneralContractHistoryState = state => selectSettingState(state).generalContractHistory;
export const selectGeneralContractHistory = state => selectGeneralContractHistoryState(state).items;

export const selectSettingsState = state => selectSettingState(state).settings;
export const selectSettings = state => selectSettingsState(state).items;

export const selectSettingsInCategories = state => {
  const settings = selectSettings(state);

  const categories = settings.reduce((obj, setting) => {
    const cat = obj[setting.category];
    setting.type = getSettingType(setting.key)

    return {
      ...obj,
      [setting.category]: cat ? [...cat, setting] : [setting],
    };
  }, {});

  return Object.entries(categories).reduce((obj, [category, categorySettings]) => {
    const orderedKeys = SETTING_CATEGORIES[category];

    return {
      ...obj,
      [category]: orderedKeys
        ? [...categorySettings].sort(
          (left, right) => orderedKeys.indexOf(left.key) - orderedKeys.indexOf(right.key)
        )
        : categorySettings,
    };
  }, {});
};

export const selecctSettingsObject = state => {
  const settings = selectSettings(state)

  return settings.reduce((obj, item) => ({...obj, [item.key] : item.value}), {})
}

/**
 * SAGAS
 */
function* fetchSetting({ payload }) {
  const resp = yield call(settingApi.fetchSetting, payload.settingId);

  if (resp.ok) {
    yield put(fetchSettingActions.success(resp.data));
  } else {
    yield put(fetchSettingActions.failure(resp.error));
  }
}

function* fetchSettings({ payload }) {
  const resp = yield call(settingApi.fetchSettings, payload);
  
  if (resp.ok) {
    yield put(fetchSettingsActions.success(resp.data));
  } else {
    yield put(fetchSettingsActions.failure(resp.error));
  }
}

function* updateSetting({ payload }) {
  const response = yield call(settingApi.updateSetting, payload.settingId, payload.settingData);

  if (response.ok) {
    yield put(updateSettingActions.success(response.data));
    yield put(setSettingEditableAction(''));
  } else {
    yield put(updateSettingActions.failure(response.error));
  }
}

function* fetchInterestSettings() {
  const resp = yield call(settingApi.fetchInterestSettings);

  if (resp.ok) {
    yield put(fetchInterestSettingsActions.success(resp.data));
  } else {
    yield put(fetchInterestSettingsActions.failure(resp.error));
  }
}

function* updateInterestSettings({ payload }) {
  const response = yield call(settingApi.updateInterestSettings, payload.interestSettings);

  if (response.ok) {
    yield put(updateInterestSettingsActions.success(response.data));
  } else {
    yield put(updateInterestSettingsActions.failure(response.error));
  }
}

function* fetchInterestSettingsHistory({ newInterests }) {
  const resp = yield call(settingApi.fetchInterestSettingsHistory);

  if (resp.ok) {
    yield put(fetchInterestSettingsHistoryActions.success(resp.data));
  } else {
    yield put(fetchInterestSettingsHistoryActions.failure(resp.error));
  }
}

function* fetchGeneralContractHistory ({ payload }) {
  const resp = yield call(settingApi.fetchGeneralContractHistory, payload);

  if(resp.ok) {
    yield put(fetchGeneralContractHistoryActions.success(resp.data))
  }
  else {
    yield put(fetchGeneralContractHistoryActions.failure(resp.error))
  }
}
function* createNewGeneralContractEntry ({ payload }){
  const { dateUpdated, role } = payload
  const resp = yield call(settingApi.createNewGeneralContractEntry, dateUpdated, role);

  if(resp.ok) {
    yield put(newGeneralContractEntryActions.success(resp.data))
    message.success('Nový záznam bol pridaný', 5)
    yield put(fetchGeneralContractHistoryActions.request())
  }
  else {
    yield put(newGeneralContractEntryActions.failure(resp.error))
  }
}
 

export function* settingSaga() {
  yield takeLatest(createActionType(FETCH_SETTING, REQUEST), fetchSetting);
  yield takeLatest(createActionType(FETCH_SETTINGS, REQUEST), fetchSettings);
  yield takeLatest(createActionType(UPDATE_SETTING, REQUEST), updateSetting);
  yield takeLatest(createActionType(FETCH_INTEREST_SETTINGS, REQUEST), fetchInterestSettings);
  yield takeLatest(createActionType(UPDATE_INTEREST_SETTINGS, REQUEST), updateInterestSettings);
  yield takeLatest(createActionType(FETCH_INTEREST_SETTINGS_HISTORY, REQUEST), fetchInterestSettingsHistory);
  yield takeLatest(createActionType(FETCH_GENERAL_CONTRACT_HISTORY, REQUEST), fetchGeneralContractHistory);
  yield takeLatest(createActionType(NEW_GENERAL_CONTRACT_ENTRY, REQUEST),createNewGeneralContractEntry);
}



// WEBPACK FOOTER //
// ./src/features/setting-management/ducks/index.js